import { CommonModule } from "@angular/common";
import { Location } from "@angular/common";
import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import {
  IonContent,
  IonicModule,
  MenuController,
  ModalController,
  NavController,
  NavParams,
  Platform,
} from "@ionic/angular";
import { TranslateModule } from "@ngx-translate/core";
import { BehaviorSubject, Observable, Subject, Subscription, combineLatest, firstValueFrom, of } from "rxjs";
import { switchMap, takeUntil } from "rxjs/operators";

//Services
import { CookiesService } from "@providers/CookiesService";
import { EventsService } from "@providers/EventsService";
import { UpdatesService } from "@providers/UpdatesService";
import { UserMigrationService } from "@providers/UserMigrationService";
import { UserService } from "@providers/UserService";
import { BufferFacade } from "@providers/facades/buffer.facade";
import { NewBaseService } from "@providers/newBase.service";
import { DeviceService } from "@services/device.service";
import { SportBookService } from "@sports-services/index";
import { TicketService } from "@sports-services/index";
import { SportService } from "@sports-services/index";
import { SportNavigationService } from "@sports-services/index";

//Stores
import { Store } from "@ngrx/store";
import { NewTicketFacade } from "@sports-features/ticket/stores";
import { SportsbookFacade } from "@sports-stores/sportsbook/sportsbook.facade";
import { SportsbookState } from "@sports-stores/sportsbook/sportsbook.reducers";
import * as sportsbookSelector from "@sports-stores/sportsbook/sportsbook.selectors";

//Pipes and directives
import { SportsExtractDatePipe } from "@sports-pipes/sports.extract-date.pipe";

//Component
import {
  SbCounterComponent,
  SbGridContentExpandedComponent,
  SbGridHeaderComponent,
  SbHeaderWithCounterComponent,
  SbIconBarComponent,
  SbOverviewScoreRegularComponent,
  SbOverviewScoreSetsComponent,
  SportsBetButtonComponent,
  SportsGridComponent,
  SportsGridContentSingleComponent,
  SportsGridContentTeamsComponent,
  SportsGroupComponent,
} from "@sports-components/index";

import { CodereSliderCsvComponent } from "@components/mso/slider/codere-slider-csv/codere-slider-csv";
import { CodereSliderComponent } from "@components/mso/slider/codere-slider/codere-slider";
import { CodereFooterComponent } from "@shared-mso-components/footer/codere-footer/codere-footer";
import { SbMarqueeBetbuilderComponent, SbMarqueeComponent } from "../components";

//Models
import { LicenseTypes } from "@models/MasterData";
import { GlobalVarsModel } from "@models/ModelGlobalsVar";
import {
  C_Banners,
  C_Event,
  C_EventMostBet,
  C_League,
  CookieNames,
  I_MarqueeItem,
  I_NewCarousel,
  SportCarrouselItem,
} from "@sports-models/index";

import { DecouplingBannersService } from "@mso-services/decouplingBanners.service";
//Others
import { CASINO_PATHS, MSO_PATHS, SPORTS_PATHS } from "@shared-constants/routes";
import { SportsGridHelperComponent } from "@sports-components/sports-grid/sports-grid-helper/sports-grid-helper.component";
import { SportsBasePage, TrackingInfo } from "@sports-pages/index";
import { TicketItemTypesEnum } from "@sports-pages/ticket/utils/ticket.constants";
import { SportsConstants } from "@sports-utils/index";
import { SCOREBOARD_TYPE } from "@sports-utils/sports.constants";
import { BetbuilderItem } from "@ticket/models";
import { temporaryPromotionConfig } from "../../../../shared/assets/js/temporaryPromotions";
import { ITemporaryPromotionConfig } from "../../../../shared/declarations/declarations";

@Component({
  selector: "home-page",
  templateUrl: "./home.page.html",
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    TranslateModule,
    SportsExtractDatePipe,
    CodereSliderComponent,
    CodereSliderCsvComponent,
    SbOverviewScoreSetsComponent,
    SbOverviewScoreRegularComponent,
    SbGridContentExpandedComponent,
    SbGridHeaderComponent,
    SbMarqueeComponent,
    SbMarqueeBetbuilderComponent,
    CodereFooterComponent,
    SbIconBarComponent,
    SbHeaderWithCounterComponent,
    SbCounterComponent,
    SportsGroupComponent,
    SportsBetButtonComponent,
    SportsGridComponent,
    SportsGridContentTeamsComponent,
    SportsGridContentSingleComponent,
    SportsGridHelperComponent,
  ],
  providers: [NavParams],
  styleUrls: ["./home.page.scss"],
})
export class HomePage extends SportsBasePage implements OnInit, OnDestroy {
  @ViewChild(IonContent) content: IonContent;

  @ViewChild("highlights") public highlights: ElementRef;

  // General
  globalVars!: GlobalVarsModel;
  temporaryPromotionConfig: ITemporaryPromotionConfig = temporaryPromotionConfig;

  // Carrousel
  carousels$: Observable<SportCarrouselItem[]>;
  sliderVisible = false;
  lstCarrousel: any[];
  lstBanners: C_Banners[] = [];
  lstNewBanners: I_NewCarousel[] = [];

  MainLeagueEvents: C_Event[];
  mainLeague: C_League;
  mainLeagueName = "";
  mainLeagueIcon = "";

  // Marquee
  marqueeData: I_MarqueeItem[] = [];

  // Directos
  LiveEventsCount: number = 0;
  liveEvents$: Observable<any>;

  // Betbuilder Marque
  betbuilderEventsData: BetbuilderItem[] = [];

  //Más Apostados New Behaviour
  sportFilters = [];
  sportFilterSelected = null;
  sportEvents: C_EventMostBet;
  betEvents$: Observable<any>;
  filtersMostBet$: BehaviorSubject<any[]>;
  currentFilter$: BehaviorSubject<any>;
  selectedMostBet$: Observable<C_EventMostBet>;
  currentSport$: BehaviorSubject<string>;
  private ngUnsubscribe$ = new Subject<boolean>();

  newBaseService = inject(NewBaseService);
  extractDate = inject(SportsExtractDatePipe);
  events = inject(EventsService);
  platform = inject(Platform);
  navParams = inject(NavParams);
  nav = inject(NavController);
  menu = inject(MenuController);
  modalController = inject(ModalController);
  navCtrl = inject(NavController);
  bufferFacade = inject(BufferFacade);
  updatesService = inject(UpdatesService);
  route = inject(ActivatedRoute);
  router = inject(Router);
  location = inject(Location);
  deviceService = inject(DeviceService);
  sportsbookFacade = inject(SportsbookFacade);

  sportsbookStore = inject(Store<SportsbookState>);

  newTicketFacade = inject(NewTicketFacade);
  userService = inject(UserService);
  sportService = inject(SportService);
  ticketService = inject(TicketService);
  sportbookService = inject(SportBookService);
  cookiesService = inject(CookiesService);
  userMigrationService = inject(UserMigrationService);
  sportNavigationService = inject(SportNavigationService);
  decouplingBannersService = inject(DecouplingBannersService);

  isMobile = false;
  isDesktop = false;

  homeData$: Observable<any>;
  homeDataSub$: Subscription;

  queryParams: any;
  sportsNavigationId = undefined;
  stateToSave = {
    scrollPosition: undefined,
    setHomeHighlighsSelectedSport: undefined,
  };
  stateToRestore = undefined;

  restoreSelectionDone = false;
  restoreScrollDone = false;

  restartingPolling = false;
  restartingPollingMessage: string;
  firstLoadDone = false;

  SCOREBOARD_TYPE = SCOREBOARD_TYPE;

  constructor(private changeDetectorRef: ChangeDetectorRef) {
    super();
    this.isDesktop = this.deviceService.isDesktop();
    this.isMobile = this.deviceService.isMobile();

    const currentUrl = this.sportNavigationService.navigationState$.getValue().currentUrl;
    this.sportsNavigationId = currentUrl.state.sportsNavigationId;
    this.queryParams = { ...currentUrl.state.queryParams };
    this.newBaseService.getVars
      .pipe(
        switchMap((data: GlobalVarsModel) => {
          this.globalVars = data;
          this.applyActionParams(this.queryParams);
          return of(null);
        }),
      )
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe();

    this.isDesktop = this.deviceService.isDesktop();
    this.sliderVisible = this.globalVars.licenseType === LicenseTypes.Nacional;
    this.sportsbookFacade.initHomeData();

    this.menu.enable(false);

    this.MainLeagueEvents = [];
    this.lstCarrousel = [];
    if (!this.globalVars.FEATURES.NewCarouselActivated) {
      this.events.subscribe("user:licenseChange", () => {
        // triggers when the license has changed, in login/logout/etc...
        this.loadBanners(true);
        this.loadCarrousel(true);
      });
    }

    this.mainLeague = new C_League([], "", "", "", 0, "", false);

    this.filtersMostBet$ = new BehaviorSubject(null);
    this.currentFilter$ = new BehaviorSubject(null);
    this.currentSport$ = new BehaviorSubject(null);

    this.homeData$ = this.sportsbookStore.select(sportsbookSelector.getHomeDataState);
    this.homeDataSub$ = this.homeData$.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((homeData) => {
      this.restartingPolling = homeData.restartingPolling;
      this.restartingPollingMessage =
        homeData.tickCounter === 0 ? "Reconectando...." : `Reconectando en ${homeData.tickCounter} segundos.`;
      this.firstLoadDone = homeData.firstLoadDone;

      this.marqueeData = homeData?.marquee;
      this.LiveEventsCount = homeData?.homeLiveEvents?.LiveEventsCount;
      this.liveEvents$ = of(homeData?.homeLiveEvents?.LiveSport);

      if (!this.sportbookService.compareTwoObjectArrays(this.sportFilters, homeData?.highlightsEvents?.sports)) {
        this.sportFilters = homeData?.highlightsEvents?.sports;
      }
      this.sportFilterSelected = homeData?.highlightsEvents?.selectedSport;
      this.sportEvents = homeData?.highlightsEvents?.events.find(
        (i) => i.SportHandle === this.sportFilterSelected?.SportHandle,
      );
      this.betbuilderEventsData = homeData?.betBuilder;

      const currentUrl = this.sportNavigationService.navigationState$.getValue().currentUrl;
      if (currentUrl?.popstate && this.sportEvents && !this.restoreSelectionDone) {
        this.sportsNavigationId = currentUrl.state.sportsNavigationId;
        this.stateToRestore = this.sportNavigationService.getRestoreStateFromHistory(this.sportsNavigationId);
        if (this.stateToRestore) {
          this.stateToSave = this.stateToRestore;
        }
        // reset history
        // this.sportNavigationService.resetHistory();
        this.restoreScrollAndSelection();
      }
      if (this.restoreSelectionDone && !this.restoreScrollDone) {
        let targetScroll;
        if (this.stateToRestore?.scrollPosition) {
          targetScroll = this.stateToRestore.scrollPosition;
        } else {
          targetScroll = 0;
        }
        this.restoreScrollDone = true;
        setTimeout(() => {
          this.content?.scrollToPoint(undefined, targetScroll, 0);
        }, 200);
      }
    });
  }

  restoreScrollAndSelection() {
    let targetSetHomeHighlighsSelectedSport;
    if (this.stateToRestore?.setHomeHighlighsSelectedSport) {
      targetSetHomeHighlighsSelectedSport = this.stateToRestore.setHomeHighlighsSelectedSport;
    }

    if (targetSetHomeHighlighsSelectedSport) {
      this.sportsbookFacade.setHomeHighlighsSelectedSport(targetSetHomeHighlighsSelectedSport);
      this.sportFilterSelected = targetSetHomeHighlighsSelectedSport;
    }
    // setTimeout(() => {
    //   this.content.scrollToPoint(undefined, targetScroll, 0);
    // }, 500);
    this.restoreSelectionDone = true;
  }

  applyActionParams(params) {
    if (params && params["codepromotion"]) {
      this.globalVars.codePromotion = params["codepromotion"];
      this.cookiesService.setCookie(
        CookieNames.CoderePromotionCode,
        this.globalVars.codePromotion,
        3 * 24 * 60 * 60 * 1000,
        true,
      );
    }
    if (params && params["addbet"]) {
      //añadir apuesta al ticket
      //!CHECK METHOD IN MASTER
      this.addTicketBetFromHome(params["addbet"]);
    }
    if (params && params["rpassword"]) {
      //ir a recordar contraseña
      this.globalVars.gotorecovery = true;
      this.openLogin();
    }
    if (params && params["openlogin"]) {
      const logincallback = () => {
        const urlafterlogin: string = params["afterloginurl"];
        if (urlafterlogin && this.globalVars.user.logged) {
          this.router.navigateByUrl(urlafterlogin);
        }
      };
      this.openLogin(logincallback);
    }
  }

  override ngOnInit(): void {
    this.loadBanners(!this.sliderVisible);
    if (!this.isDesktop) {
      this.loadCarrousel();
    }
    if (this.globalVars?.firstLoad) {
      this.trackingService.trackEvent(["loadHomePage", "", "", "Carga de home", "event"]);
      this.globalVars.firstLoad = false;
    } else {
      this.trackingService.trackEvent(["goToIndexPage", "", "", "Ir a la pagina index", "event"]);
    }
  }

  ngAfterContentInit() {
    if (this.globalVars.forceToOpenLogin && !this.globalVars.user.logged) {
      this.globalVars.forceToOpenLogin = false;
      this.openLogin();
    }
  }

  ngOnDestroy(): void {
    // this.globalVars.lstBanners = [];
    this.ngUnsubscribe$.next(true);
    this.ngUnsubscribe$.complete();
    this.sportNavigationService.injectLastStateToHistory(this.sportsNavigationId, this.stateToSave);
  }

  loadBanners(clearCache = false) {
    if (clearCache || (this.globalVars.lstBanners && this.globalVars.lstBanners.length === 0)) {
      this.lstNewBanners = [];
      this.lstBanners = [];
      const sharepointImagesList: string = this.isDesktop ? "bannerswebappPC" : "Bannerswebapp";
      if (this.globalVars.FEATURES.NewCarouselActivated) {
        const request = this.globalVars.FEATURES.MSO_DecouplingBanners
          ? firstValueFrom(this.decouplingBannersService.getBanners(sharepointImagesList))
          : this.userService.getNewBanners(sharepointImagesList);
        request
          .then((data: I_NewCarousel[]) => {
            this.globalVars.lstBanners = data || [];
            this.lstNewBanners = data || [];
            this.sliderVisible = true;
            //refresh footer
            // this.events.publish('footer:refresh', this.content);
            this.changeDetectorRef.detectChanges();
          })
          .catch((err) => {
            console.error(err);
          });
      } else {
        this.userService
          .getBanners(sharepointImagesList)
          .pipe(takeUntil(this.ngUnsubscribe$))
          .subscribe({
            next: (data: any) => {
              this.lstBanners = data || [];
              this.globalVars.lstBanners = data || [];
              this.sliderVisible = true;
              //refresh footer
              // this.events.publish('footer:refresh', this.content);
              this.changeDetectorRef.detectChanges();
            },
            error: (error: any) => {
              console.error(error);
            },
          });
      }
    } else {
      if (this.globalVars.FEATURES.NewCarouselActivated) {
        this.lstNewBanners = this.globalVars.lstBanners;
      } else {
        this.lstBanners = this.globalVars.lstBanners;
      }
      this.sliderVisible = true;
      this.changeDetectorRef.detectChanges();
    }
  }

  loadCarrousel(clearCache: boolean = false) {
    this.carousels$ = combineLatest(
      this.userService.getCarrousel(),
      this.globalVars.sportItemsId$,
      (carrousel: any, sportItemsId: any) => {
        const navbarItems = [];
        if (!sportItemsId) {
          return navbarItems;
        }
        carrousel?.items?.forEach((carrouselItem) => {
          let actionLink = "";
          switch (carrouselItem?.actionData) {
            case "soccer":
            case "basketball":
            case "tennis":
            case "american_football":
            case "baseball":
              actionLink = this.setActionLink(carrouselItem?.actionLink, carrouselItem?.actionData, sportItemsId);
              break;
            case SportsConstants.tournamentLiteral:
              actionLink = carrouselItem?.actionLink;
              break;
            default:
              actionLink = carrouselItem?.actionLink;
              break;
          }
          navbarItems.push(
            new SportCarrouselItem(carrouselItem.label, carrouselItem.img, actionLink, carrouselItem.actionData),
          );
        });
        return navbarItems;
      },
    );
  }

  clickItemCarrouselSportBook(item: SportCarrouselItem) {
    const [link, label, additionalData] = [item.actionLink, item.Name, item.actionData];
    const linkPage = {
      page: link.split("?")[0],
      params: link.split("?")[1],
    };

    if (additionalData === "LobbyCasino") {
      const lobbies = this.globalVars.rootScope.lobbies;
      const lobby = lobbies.find((lb) => lb.LobbyName === item.actionLink);
      if (lobby) {
        this.globalVars.rootScope.openLobby(lobby);
      } else {
        this.router.navigate([`/${CASINO_PATHS.BASE}/${item.Name}Page`], {
          queryParams: { lobby: item.actionLink },
        });
      }
      this.trackingService.trackEvent(["OpenLobbyFromCarrusel", link, label, link, "event"]);
    } else if (additionalData === "LobbyJackpots") {
      this.globalVars.rootScope.openJackpots();
    } else if (link === "misApuestasPage") {
      this.router.navigate([`/${SPORTS_PATHS.MisApuestasPage}`], {
        state: { params: this.globalVars.rootScope.storedParams },
      });
    } else if (link === MSO_PATHS.ContactPage || link === MSO_PATHS.PromotionsPage) {
      this.router.navigate([`/${MSO_PATHS.BASE}/${item.actionLink}`], {
        state: { params: this.globalVars.rootScope.storedParams },
      });
    } else {
      this.globalVars.rootScope.storedParams = this.globalVars.rootScope.getObjectParam(linkPage.params);
      this.globalVars.rootScope.tryToGoPage(linkPage.page);
      this.router.navigate([`/${linkPage.page}`], { state: { params: this.globalVars.rootScope.storedParams } });
    }
    this.trackingService.trackEvent(["AccessFromCarrusel", label, linkPage.page, link, "event"]);
  }

  clickBanner(theBanner: C_Banners): void {
    if (theBanner.Target === "_self") {
      if (theBanner.Url.search("http") !== -1) {
        if (theBanner.Url.search(this.globalVars.DOMAIN) !== -1) {
          if (theBanner.Url.search("#") !== -1) {
            const num = theBanner.Url.search("#");
            const url = theBanner.Url.substring(num + 2);
            this.globalVars.rootScope.extTryToGoPage(url);
          }
        }
      } else {
        if (theBanner.Url.search("#") !== -1) {
          const num = theBanner.Url.search("#");
          const url = theBanner.Url.substring(num + 2);
          this.globalVars.rootScope.extTryToGoPage(url);
        } else if (theBanner.Url.search("addbet") !== -1) {
          const equal = theBanner.Url.search("=");
          const bets = theBanner.Url.substring(equal + 1);
          this.addTicketBetFromHome(bets);
        }
      }
    } else {
      window.open(theBanner.Url);
    }
  }

  bet(game: any, event: any, gameName: string, type: string) {
    const data = {
      ...game,
      EventDate: this.extractDate.transform(event.StartDate),
      EventName: event.Name,
      GameName: gameName,
    };
    this.newTicketFacade.checkSelection(data, data.NodeId, TicketItemTypesEnum.LINE_ITEM);
  }

  public navigate(market: any, route?: string, sport?: any, gameType?: any) {
    if (gameType) {
      return;
    }

    if (market && !market?.SportName && market.SportHandle === "basketball") {
      market = {
        ...market,
        SportName: "Baloncesto",
      };
    }
    switch (route) {
      case "live":
        this.router.navigate([`/${SPORTS_PATHS.DirectosPage}`]);
        break;
      case "market":
      case "live-market": {
        const component = market.isLive ? SPORTS_PATHS.NowLiveDetailPage : SPORTS_PATHS.MercadosPage;
        const eventType = market.isLive ? "pushEventLive" : "pushEvent";
        this.router.navigate([`/${component}`], {
          state: { params: market },
        });
        this.trackingService.trackEvent([eventType, market.NodeId, window.location.hash, market.Name, "event"]);
        break;
      }
    }
  }

  trackBy(index, item) {
    return item.NodeId;
  }

  openPromos() {
    this.router.navigate([`/${MSO_PATHS.PromotionsPage}`], {
      queryParams: { url: this.temporaryPromotionConfig.promosInfoPageUrl },
    });
  }

  async openTemporaryPromotion() {
    this.trackingService.trackEvent([
      "OpenTemporaryPromotionFromHome",
      "",
      this.route.component.name, // TODO REVISAR QUE FUNCIONA  this.globalVars.currentPage.name
      "",
      "event",
    ]);
    if (this.isDesktop) {
      const modal = await this.modalController.create({
        component: "ExternalPage",
        componentProps: {
          url: this.temporaryPromotionConfig.temporaryPromotionSiteUrl,
        },
      });
      modal.present();
      modal.onDidDismiss().then((moreinfo) => {
        if (moreinfo) {
          this.router.navigate([`/${MSO_PATHS.PromotionsPage}`], {
            queryParams: { url: this.temporaryPromotionConfig.promosInfoPageUrl },
          });
        }
      });
    } else {
      this.router.navigate(["/ExternalPage"], {
        queryParams: { url: this.temporaryPromotionConfig.temporaryPromotionSiteUrl },
      });
    }
  }

  setCurrentFilter(filter) {
    this.sportsbookFacade.setHomeHighlighsSelectedSport(filter.SportHandle);
    this.stateToSave = {
      ...this.stateToSave,
      setHomeHighlighsSelectedSport: filter.SportHandle,
    };
  }

  async openLogin(callback?: () => void) {
    if (this.globalVars.user.logged) {
      this.events.publish("load:afterlogin");
      return;
    }
    this.globalVars.rootScope.openLogin(callback);
  }

  setActionLink(actionLink: string, sporthandle: any, sportItemsId: any) {
    const newLink = `${actionLink}?SportHandle=${sporthandle}&NodeId=${sportItemsId[sporthandle]?.id}&Name=${sportItemsId[sporthandle]?.name}`;
    return newLink;
  }

  addTicketBetFromHome(itemsString: string) {
    const items = itemsString.split("-");
    for (let i = 0; i < items.length; i++) {
      this.newTicketFacade.addBetByLink(+itemsString);
    }

    this.trackingService.trackEvent([
      "AddBetExt",
      itemsString,
      "",
      "A\u00F1adir via banner apuesta por id",
      "event",
    ]);
  }

  getTrackingInfo(): TrackingInfo {
    return {
      uri: SPORTS_PATHS.HomePage,
      description: "Home page",
      additionalData: null,
    };
  }

  onScroll(ev) {
    const newStateToSave = {
      ...this.stateToSave,
      scrollPosition: ev.detail.scrollTop,
    };
    this.stateToSave = newStateToSave;
  }
}
